"use client";

// "use client" hoja interactiva: copy localizada (useT/useLocale) + reveals + canvas hijo del hero — contexto cliente.
import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { ExpressiveButton } from "@/components/ui/expressive-button";
import { localePath } from "@/lib/i18n/locales";
import { useLocale, useT } from "@/lib/use-i18n";
import { AccentTail, Reveal } from "./Reveal";
import { HeroAmbientCanvas } from "./HeroAmbientCanvas";

/**
 * Landing-only pricing hook: no full plan grid (that lives on /pricing).
 * Direct, confident CTA toward the dedicated pricing destination.
 */
export function PricingTeaser() {
  const $ = _c(37);
  const t = useT();
  const locale = useLocale();
  let t0;
  if ($[0] !== locale) {
    t0 = path => localePath(locale, path);
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const lp = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <HeroAmbientCanvas transparent={true} className="absolute inset-x-0 -bottom-[35%] h-[90%] w-full pointer-events-none" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t("pricingTeaserTitle");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <h2 id="pricing-teaser-title" className="text-section-heading text-expressive-emphasized max-w-[22ch] text-foreground font-semibold tracking-tight text-pretty"><AccentTail text={t2} /></h2>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("pricingTeaserBody");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <p className="mt-4 max-w-[46ch] text-[15px] leading-relaxed text-muted-foreground text-pretty">{t4}</p>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <div className="text-center md:text-left">{t3}{t5}</div>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== lp) {
    t7 = lp("/pricing");
    $[14] = lp;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("pricingTeaserCta");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <span>{t8}</span>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ArrowRight className="size-4" aria-hidden="true" />;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t7 || $[22] !== t9) {
    t11 = <ExpressiveButton variant="hero" size="lg" className="hero-cta hero-cta--primary font-medium border-0 rounded-full w-full sm:w-auto shadow-sm transition-all duration-200 hover:-translate-y-px hover:shadow-md" asChild={true}><Link href={t7} data-testid="pricing-teaser-cta">{t9}{t10}</Link></ExpressiveButton>;
    $[21] = t7;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== lp) {
    t12 = lp("/chat");
    $[24] = lp;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("chatTitle");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t12 || $[29] !== t13) {
    t14 = <Link href={t12} className="text-center text-[13px] text-muted-foreground transition-colors hover:text-foreground underline underline-offset-4 decoration-border/60 hover:decoration-foreground">{t13}</Link>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t11 || $[32] !== t14) {
    t15 = <div className="flex flex-col items-stretch gap-3 sm:flex-row md:flex-col md:items-center w-full md:w-auto shrink-0">{t11}{t14}</div>;
    $[31] = t11;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15 || $[35] !== t6) {
    t16 = <section aria-labelledby="pricing-teaser-title" className="landing-section landing-section--soft relative" data-testid="pricing-teaser"><div className="mx-auto max-w-container-wide gutter-fluid"><Reveal className="pricing-teaser-band lovable-card hardware-surface relative overflow-hidden p-6 sm:p-8 md:p-12 rounded-[var(--radius-panel)] border border-border/80 dark:border-white/10 shadow-[var(--shadow-panel)] dark:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.6),inset_0_1px_0_0_rgba(255,255,255,0.08)]">{t1}<div className="pricing-teaser-inner relative z-[1] flex flex-col items-center justify-between gap-8 md:flex-row">{t6}{t15}</div></Reveal></div></section>;
    $[34] = t15;
    $[35] = t6;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  return t16;
}